import { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import ProgressIndicator from "./ProgressIndicator";
import DateStep from "./steps/DateStep";
import NamesStep from "./steps/NamesStep";
import VenueStep from "./steps/VenueStep";
import ServicesStep from "./steps/ServicesStep";
import ContactStep from "./steps/ContactStep";
import SuccessStep from "./steps/SuccessStep";
import AvailabilityConfirmation from "./steps/AvailabilityConfirmation";

interface FormData {
  date: Date | undefined;
  firstName: string;
  lastName: string;
  venue: string;
  services: string[];
  email: string;
  phone: string;
  message: string;
}

interface MultiStepFormProps {
  calLink?: string;
}

// Helper to build quiz notes from URL params
const buildQuizNotes = (searchParams: URLSearchParams): string => {
  const quizBudget = searchParams.get("quiz_budget");
  const quizServices = searchParams.get("quiz_services");
  const quizGuests = searchParams.get("quiz_guests");
  const packageName = searchParams.get("package");
  
  if (!quizBudget && !quizServices && !quizGuests) return "";
  
  const budgetLabels: Record<string, string> = {
    "under-1500": "Under $1,500",
    "1500-2500": "$1,500 - $2,500",
    "2500-4000": "$2,500 - $4,000",
    "4000-plus": "$4,000+",
  };
  
  const serviceLabels: Record<string, string> = {
    "photo": "Photography",
    "video": "Videography",
    "photobooth": "Photo Booth",
  };
  
  const guestLabels: Record<string, string> = {
    "small": "Under 50 guests",
    "medium": "50-150 guests",
    "large": "150-300 guests",
    "xlarge": "300+ guests",
  };
  
  let notes = "📋 Quiz Results:\n";
  if (packageName) notes += `• Recommended Package: ${packageName}\n`;
  if (quizBudget) notes += `• Budget: ${budgetLabels[quizBudget] || quizBudget}\n`;
  if (quizServices) {
    const services = quizServices.split(",").map(s => serviceLabels[s] || s).join(", ");
    notes += `• Services Interested: ${services}\n`;
  }
  if (quizGuests) notes += `• Expected Guests: ${guestLabels[quizGuests] || quizGuests}\n`;
  
  return notes;
};

const MultiStepForm = ({ calLink }: MultiStepFormProps) => {
  const [searchParams] = useSearchParams();
  const selectedPackage = searchParams.get("package");
  const hasQuizResults = searchParams.get("quiz_budget") || searchParams.get("quiz_services") || searchParams.get("quiz_guests");
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  
  // Build initial message with quiz notes if present
  const initialMessage = buildQuizNotes(searchParams);
  
  const [formData, setFormData] = useState<FormData>({
    date: undefined,
    firstName: "",
    lastName: "",
    venue: "",
    services: [],
    email: "",
    phone: "",
    message: initialMessage,
  });

  const totalSteps = 6;

  const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, totalSteps + 1));
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  // Scroll to top of form when step changes
  useEffect(() => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [currentStep]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.functions.invoke('submit-inquiry', {
        body: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone || undefined,
          weddingDate: formData.date?.toISOString().split('T')[0],
          venue: formData.venue || undefined,
          services: formData.services,
          message: formData.message || undefined,
          selectedPackage: selectedPackage || undefined
        }
      });

      if (error) throw error;

      nextStep(); // Go to success step
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error("Something went wrong. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <DateStep
            value={formData.date}
            onChange={(date) => setFormData(prev => ({ ...prev, date }))}
            onNext={nextStep}
          />
        );
      case 2:
        return (
          <AvailabilityConfirmation
            weddingDate={formData.date!}
            onNext={nextStep}
          />
        );
      case 3:
        return (
          <NamesStep
            firstName={formData.firstName}
            lastName={formData.lastName}
            onFirstNameChange={(value) => setFormData(prev => ({ ...prev, firstName: value }))}
            onLastNameChange={(value) => setFormData(prev => ({ ...prev, lastName: value }))}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 4:
        return (
          <VenueStep
            venue={formData.venue}
            onVenueChange={(value) => setFormData(prev => ({ ...prev, venue: value }))}
            onNext={nextStep}
            onBack={prevStep}
            firstName={formData.firstName}
          />
        );
      case 5:
        return (
          <ServicesStep
            services={formData.services}
            onServicesChange={(services) => setFormData(prev => ({ ...prev, services }))}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 6:
        return (
          <ContactStep
            email={formData.email}
            phone={formData.phone}
            message={formData.message}
            onEmailChange={(value) => setFormData(prev => ({ ...prev, email: value }))}
            onPhoneChange={(value) => setFormData(prev => ({ ...prev, phone: value }))}
            onMessageChange={(value) => setFormData(prev => ({ ...prev, message: value }))}
            onSubmit={handleSubmit}
            onBack={prevStep}
            isSubmitting={isSubmitting}
            firstName={formData.firstName}
          />
        );
      case 7:
        return (
          <SuccessStep
            firstName={formData.firstName}
            calLink={calLink}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div ref={formRef} className="w-full max-w-lg mx-auto scroll-mt-24">
      {selectedPackage && currentStep < 7 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 flex flex-wrap items-center justify-center gap-2"
        >
          <div className="flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
            <Sparkles className="h-4 w-4" />
            <span>Inquiring about: <strong>{selectedPackage}</strong></span>
          </div>
          {hasQuizResults && (
            <div className="rounded-full bg-accent/20 px-3 py-1 text-xs font-medium text-accent-foreground">
              Quiz results saved ✓
            </div>
          )}
        </motion.div>
      )}
      
      <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} />
      
      <div className="bg-card rounded-2xl p-6 md:p-8 shadow-xl border border-border">
        <AnimatePresence mode="wait">
          {renderStep()}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default MultiStepForm;
